import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, fmtMoney, fmtRoas, fileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EmptyState, Spinner, SectionTitle, roasTone } from '@/components/common';
import { CreativeDialog } from '@/components/CreativeDialog';
import { toast } from 'sonner';
import { Film, Plus, Sparkles, Trash2, Link as LinkIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
  draft: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  live: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  paused: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  winner: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
  killed: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

export default function Creatives() {
  const [creatives, setCreatives] = useState(null);
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.get('/creatives'), api.get('/products')]);
    setCreatives(c.data);
    setProducts(p.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const busy = (creatives || []).some((c) => c.diagnosis_status === 'diagnosing');
    clearInterval(pollRef.current);
    if (busy) pollRef.current = setInterval(load, 5000);
    return () => clearInterval(pollRef.current);
  }, [creatives, load]);

  const diagnose = async (c) => {
    await api.post(`/creatives/${c.id}/diagnose`);
    setCreatives((cs) => cs.map((x) => (x.id === c.id ? { ...x, diagnosis_status: 'diagnosing' } : x)));
    toast.info('AI diagnosing creative...');
  };

  const del = async (c) => {
    await api.delete(`/creatives/${c.id}`);
    toast.success('Creative deleted');
    load();
  };

  const filtered = useMemo(() => (creatives || []).filter((c) =>
    (productFilter === 'all' || c.product_id === productFilter) &&
    (statusFilter === 'all' || c.status === statusFilter)), [creatives, productFilter, statusFilter]);

  const diagnosed = useMemo(() => filtered.filter((c) => c.ai_diagnosis), [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Creative Testing Tracker</h1>
          <div className="page-accent-bar" />
          <p className="mt-2 text-sm text-muted-foreground">Every ad you test, tagged by avatar / angle / hook, with live numbers and AI verdicts.</p>
        </div>
        <Button data-testid="creatives-add-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="accent-btn gap-2"><Plus className="h-4 w-4" /> Add Creative</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={productFilter} onValueChange={setProductFilter}>
          <SelectTrigger data-testid="creatives-product-filter" className="w-[200px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="creatives-status-filter" className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {['draft', 'live', 'paused', 'winner', 'killed'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {creatives === null ? (
        <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState testId="creatives-empty" icon={Film} title="No creatives tracked"
          subtitle="Add your first ad creative — upload the file or paste a link, tag the angle and hook, then log spend and results from Ads Manager."
          action={<Button data-testid="creatives-empty-add-button" onClick={() => { setEditing(null); setDialogOpen(true); }} className="gap-2"><Plus className="h-4 w-4" />Add Creative</Button>} />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table data-testid="creative-testing-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Creative</TableHead><TableHead>Product</TableHead><TableHead>Angle / Hook</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Spend</TableHead>
                  <TableHead className="text-right">CTR</TableHead><TableHead className="text-right">CPC</TableHead>
                  <TableHead className="text-right">CPM</TableHead><TableHead className="text-right">ATC</TableHead>
                  <TableHead className="text-right">Purch</TableHead><TableHead className="text-right">CPA</TableHead>
                  <TableHead className="text-right">ROAS</TableHead><TableHead>AI</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} data-testid="creative-row" className="cursor-pointer" onClick={() => { setEditing(c); setDialogOpen(true); }}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.file_url ? (
                          /\.(mp4|mov|webm|avi)$/i.test(c.file_url)
                            ? <video src={fileUrl(c.file_url)} className="h-9 w-9 rounded object-cover" muted />
                            : <img src={fileUrl(c.file_url)} alt="" className="h-9 w-9 rounded object-cover" />
                        ) : c.external_link ? (
                          <a href={c.external_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="flex h-9 w-9 items-center justify-center rounded bg-muted"><LinkIcon className="h-4 w-4 text-muted-foreground" /></a>
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded bg-muted"><Film className="h-4 w-4 text-muted-foreground" /></div>
                        )}
                        <div>
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-[11px] text-muted-foreground">{c.format} · {c.platform}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{c.product_name}</TableCell>
                    <TableCell className="max-w-[160px]">
                      <p className="truncate text-xs">{c.angle || '—'}</p>
                      <p className="truncate text-[11px] text-muted-foreground">{c.hook || ''}</p>
                    </TableCell>
                    <TableCell><span className={cn('rounded px-1.5 py-0.5 text-xs font-medium', STATUS_STYLES[c.status])}>{c.status}</span></TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(c.spend)}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{c.ctr}%</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(c.cpc)}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{fmtMoney(c.cpm)}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{c.atc}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{c.purchases}</TableCell>
                    <TableCell className="text-right font-mono text-sm tabular-nums">{c.purchases > 0 ? fmtMoney(c.cpa) : '—'}</TableCell>
                    <TableCell className={cn('text-right font-mono text-sm font-semibold tabular-nums', roasTone(c.roas, c.spend))}>{fmtRoas(c.roas)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button data-testid="creative-diagnose-button" variant="outline" size="sm" className="gap-1" disabled={c.diagnosis_status === 'diagnosing'} onClick={() => diagnose(c)}>
                        {c.diagnosis_status === 'diagnosing' ? <Spinner /> : <Sparkles className="h-3.5 w-3.5" />}
                      </Button>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button data-testid="creative-delete-button" variant="ghost" size="icon" aria-label="Delete creative" onClick={() => del(c)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* AI diagnoses */}
      {diagnosed.length > 0 && (
        <div className="space-y-3">
          <SectionTitle>AI Creative Diagnoses</SectionTitle>
          {diagnosed.map((c) => (
            <Card key={c.id} data-testid="creative-diagnosis-card" className="border-primary/20">
              <CardContent className="p-4 text-sm">
                <p className="font-semibold">{c.name} — <span className="text-primary">{c.ai_diagnosis.verdict}</span></p>
                <p className="mt-1">{c.ai_diagnosis.diagnosis}</p>
                <p className="mt-1"><span className="font-semibold">Next action:</span> {c.ai_diagnosis.next_action}</p>
                {c.ai_diagnosis.iteration_ideas?.length > 0 && (
                  <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">{c.ai_diagnosis.iteration_ideas.map((idea) => <li key={idea}>{idea}</li>)}</ul>
                )}
                {c.ai_diagnosis.missing_data?.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">Missing data: {c.ai_diagnosis.missing_data.join(', ')}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreativeDialog open={dialogOpen} onOpenChange={setDialogOpen} creative={editing} products={products} onSaved={load} />
    </div>
  );
}
